import { easeInOutCubic } from "utils/animation/easing";

/**
 * Scrolls an element into view.
 *
 * @param element The element to scroll to.
 * @param alignment How to position the element relative to the viewport.
 * @param duration How long the animation should take, in milliseconds.
 */
export function scrollIntoView(
  element: HTMLElement,
  alignment: "top" | "bottom" | "center",
  duration = 800
): void {
  const rect = element.getBoundingClientRect();
  const currentDocumentScroll = document.documentElement.scrollTop;
  let finalDocumentScroll = currentDocumentScroll;
  const windowHeight = window.innerHeight;

  if (alignment === "bottom") {
    finalDocumentScroll = currentDocumentScroll + (rect.bottom - windowHeight);
  } else if (alignment === "top") {
    finalDocumentScroll = currentDocumentScroll + rect.top;
  } else if (alignment === "center") {
    const verticalMargin = (windowHeight - rect.height) / 2;
    finalDocumentScroll = currentDocumentScroll + rect.top - verticalMargin;
  }

  doScrollAnimation({
    from: currentDocumentScroll,
    to: finalDocumentScroll,
    duration,
    startTime: performance.now(),
  });
  document.documentElement.scrollTop = finalDocumentScroll;
}

interface ScrollAnimationOptions {
  from: number;
  to: number;
  duration: number;
  startTime: number;
}

function doScrollAnimation(options: ScrollAnimationOptions) {
  requestAnimationFrame(() => {
    const timePassed = performance.now() - options.startTime;
    const progress = Math.min(timePassed / options.duration, 1);
    const easedProgress = easeInOutCubic(progress);
    const scrollPosition =
      options.from + (options.to - options.from) * easedProgress;
    document.documentElement.scrollTop = scrollPosition;

    if (progress < 1) {
      doScrollAnimation(options);
    }
  });
}
